import React, { useState, useEffect } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { MobilePage, MobileListRow, MobileRowAction, MobileEmptyState, mobile } from '../components/mobile'
import { ReviewJobQuickPanel } from '../components/review/ReviewJobQuickPanel'
import {
  SearchIcon,
  FilterIcon,
  EyeIcon,
  CheckCircleIcon,
  ClockIcon,
  AlertCircleIcon,
  CalendarIcon,
} from 'lucide-react'
import { supabase } from '../lib/supabase'
import { ColumnSelector } from '../components/ColumnSelector'
import { useColumnSelector } from '../hooks/useColumnSelector'
import { useDivision } from '../contexts/DivisionContext'
import { usePermissions } from '../hooks/usePermissions'
import { fetchReviewQueue, type ReviewQueueJob, type ReviewQueueStats } from '../api/reviewQueue'
import { getErrorMessage } from '../lib/errors'

export function Review() {
  const navigate = useNavigate()
  const { currentDivision } = useDivision()
  const { can, loading: permLoading } = usePermissions()
  const [jobs, setJobs] = useState<ReviewQueueJob[]>([])
  const [stats, setStats] = useState<ReviewQueueStats>({
    pendingReview: 0,
    approved: 0,
    needsRevision: 0,
    total: 0,
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [panelJob, setPanelJob] = useState<ReviewQueueJob | null>(null)

  useEffect(() => {
    if (permLoading || !can('review.view')) return
    fetchJobs()
  }, [currentDivision, permLoading])

  const fetchJobs = async () => {
    try {
      setLoading(true)
      setError(null)

      const { jobs: queueJobs, stats: queueStats } = await fetchReviewQueue(currentDivision)
      setJobs(queueJobs)
      setStats(queueStats)
    } catch (err) {
      console.error('Error fetching jobs:', err)
      setError(getErrorMessage(err, 'Failed to load jobs. Please try again.'))
      setJobs([])
      setStats({ pendingReview: 0, approved: 0, needsRevision: 0, total: 0 })
    } finally {
      setLoading(false)
    }
  }

  const handleApprove = async (jobId: string) => {
    if (!can('review.approve')) {
      alert('You do not have permission to approve jobs.')
      return
    }
    try {
      const { error: updateError } = await supabase
        .from('jobs')
        .update({ status: 'approved' })
        .eq('id', jobId)

      if (updateError) throw updateError

      // Refresh jobs list
      await fetchJobs()
    } catch (err) {
      console.error('Error approving job:', err)
      alert('Failed to approve job. Please try again.')
    }
  }

  const handleView = (jobId: string) => {
    navigate(`/review/${jobId}`)
  }

  const openQuickView = (job: ReviewQueueJob, e?: React.MouseEvent) => {
    e?.stopPropagation()
    setPanelJob(job)
  }

  // Map database status to display status
  const getDisplayStatus = (status: string) => {
    if (
      status === 'completed' ||
      status === 'pending_review' ||
      status === 'under_review'
    ) {
      return 'pending-review'
    }
    if (status === 'qc_failed') return 'needs-revision'
    return status
  }

  const filteredJobs = jobs.filter((job) => {
    const displayStatus = getDisplayStatus(job.status)
    const customerName = job.customer?.company_name || ''
    const propertyAddress = job.property?.address || ''
    const propertyCity = job.property?.city || ''
    const propertyDisplay = `${propertyAddress}${propertyCity ? `, ${propertyCity}` : ''}`

    const matchesSearch =
      job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      propertyDisplay.toLowerCase().includes(searchTerm.toLowerCase())

    const matchesStatus =
      statusFilter === 'all' ||
      displayStatus === statusFilter ||
      (statusFilter === 'pending-review' && (job.status === 'pending_review' || job.status === 'under_review')) ||
      (statusFilter === 'needs-revision' && job.status === 'qc_failed')

    return matchesSearch && matchesStatus
  })

  const REVIEW_COLUMNS = [
    { id: 'job', label: 'Job' },
    { id: 'customer', label: 'Customer' },
    { id: 'completed', label: 'Completed' },
    { id: 'technicians', label: 'Technicians' },
    { id: 'photos', label: 'Photos' },
    { id: 'status', label: 'Status' },
    { id: 'actions', label: 'Actions' },
  ]
  const { visibleColumns, toggleColumn } = useColumnSelector(REVIEW_COLUMNS, { storageKey: 'review' })

  const getStatusColor = (status: string) => {
    const displayStatus = getDisplayStatus(status)
    switch (displayStatus) {
      case 'pending-review':
        return 'bg-yellow-100 text-yellow-800'
      case 'approved':
        return 'bg-green-100 text-green-800'
      case 'needs-revision':
        return 'bg-red-100 text-red-800'
      case 'completed':
        return 'bg-blue-100 text-blue-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusIcon = (status: string) => {
    const displayStatus = getDisplayStatus(status)
    switch (displayStatus) {
      case 'pending-review':
        return ClockIcon
      case 'approved':
        return CheckCircleIcon
      case 'needs-revision':
        return AlertCircleIcon
      default:
        return ClockIcon
    }
  }

  const formatStatusLabel = (status: string) => {
    const displayStatus = getDisplayStatus(status)
    return displayStatus.replace('-', ' ')
  }

  const getCompletedDate = (job: ReviewQueueJob) => {
    return job.actual_end_time || job.actual_end_time || ''
  }

  if (!permLoading && !can('review.view')) {
    return <Navigate to="/access-denied" replace />
  }

  return (
    <MobilePage>
      <div>
        <h1 className={mobile.title}>Office Review</h1>
          <p className="mt-1 text-gray-600">
            Review completed jobs and approve for client delivery
          </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-3 sm:p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Pending Review</p>
              <p className="mt-2 text-2xl font-bold text-gray-900">
                {loading ? (
                  <span className="inline-block h-8 w-8 bg-gray-100 rounded animate-pulse" />
                ) : (
                  stats.pendingReview
                )}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-yellow-50 text-yellow-600">
              <ClockIcon className="w-6 h-6" />
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Approved</p>
              <p className="mt-2 text-2xl font-bold text-gray-900">
                {loading ? (
                  <span className="inline-block h-8 w-8 bg-gray-100 rounded animate-pulse" />
                ) : (
                  stats.approved
                )}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-green-50 text-green-600">
              <CheckCircleIcon className="w-6 h-6" />
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Needs Revision</p>
              <p className="mt-2 text-2xl font-bold text-gray-900">
                {loading ? (
                  <span className="inline-block h-8 w-8 bg-gray-100 rounded animate-pulse" />
                ) : (
                  stats.needsRevision
                )}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-red-50 text-red-600">
              <AlertCircleIcon className="w-6 h-6" />
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Jobs</p>
              <p className="mt-2 text-2xl font-bold text-gray-900">
                {loading ? (
                  <span className="inline-block h-8 w-8 bg-gray-100 rounded animate-pulse" />
                ) : (
                  stats.total
                )}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-blue-50 text-blue-600">
              <CalendarIcon className="w-6 h-6" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
            <div className="flex flex-col sm:flex-row gap-4 flex-1">
              <div className="relative flex-1 max-w-md">
                <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search jobs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={mobile.field + ' pl-10'}
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className={mobile.field}
              >
                <option value="all">All Status</option>
                <option value="pending-review">Pending Review</option>
                <option value="approved">Approved</option>
                <option value="needs-revision">Needs Revision</option>
              </select>
            </div>
            <ColumnSelector
              columns={REVIEW_COLUMNS}
              visibleColumns={visibleColumns}
              onToggle={toggleColumn}
            />
            <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
              <FilterIcon className="w-5 h-5" />
              Advanced Filters
            </button>
        </div>
      </div>

      <div className="native-mobile-list space-y-2">
        {!loading && !error && filteredJobs.length === 0 && (
          <MobileEmptyState
            title="No jobs found for review"
            description={
              currentDivision
                ? `No jobs awaiting review in ${currentDivision.name}. Try All Divisions or check End of Day for today's completed work.`
                : 'Jobs awaiting office review will appear here after field crews finish work.'
            }
          />
        )}
        {!loading && !error && filteredJobs.map((job) => {
          const customerName = job.customer?.company_name || 'N/A'
          const propertyAddress = job.property?.address || ''
          const propertyCity = job.property?.city || ''
          const propertyDisplay = `${propertyAddress}${propertyCity ? `, ${propertyCity}` : ''}`
          const completedDate = getCompletedDate(job)
          return (
            <MobileListRow
              key={job.id}
              onClick={() => handleView(job.id)}
              title={job.title}
              subtitle={customerName}
              meta={
                <>
                  {propertyDisplay && <div className="truncate">{propertyDisplay}</div>}
                  {job.techs.length > 0 && <div>Crew: {job.techs.join(', ')}</div>}
                  {completedDate && (
                    <div>Completed: {new Date(completedDate).toLocaleDateString()}</div>
                  )}
                  <div>Photos: {job.photos || 0}</div>
                </>
              }
              badges={
                <span className={`${mobile.badge} ${
                  job.status === 'pending_review' || job.status === 'pending-review'
                    ? 'bg-purple-100 text-purple-800'
                    : job.status === 'approved'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-amber-100 text-amber-800'
                }`}>
                  {String(job.status || '').replace(/_/g, ' ').replace(/-/g, ' ')}
                </span>
              }
              footer={
                <MobileRowAction tone="primary" onClick={() => setPanelJob(job)}>
                  <EyeIcon className="w-5 h-5" aria-hidden />
                  <span className="sr-only">Quick view</span>
                </MobileRowAction>
              }
            />
          )
        })}
      </div>

      <div className="native-desktop-table bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Jobs Pending Review</h2>
          </div>
          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-gray-600">Loading jobs...</p>
            </div>
          ) : error ? (
            <div className="p-12 text-center">
              <AlertCircleIcon className="mx-auto h-12 w-12 text-red-500" />
              <p className="mt-4 text-gray-600">{error}</p>
            </div>
          ) : filteredJobs.length === 0 ? (
            <div className="p-12 text-center">
              <CalendarIcon className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-4 text-gray-600">No jobs found for review</p>
              <p className="mt-2 text-sm text-gray-500">
                {searchTerm || statusFilter !== 'all'
                  ? 'Try adjusting your search or filter criteria'
                  : currentDivision
                    ? `No jobs awaiting review in ${currentDivision.name}. Try All Divisions or check End of Day for today's completed work.`
                    : 'Jobs awaiting office review will appear here after field crews finish work.'}
              </p>
            </div>
          ) : (
            <div className="w-full">
              <table className="w-full table-fixed">
                <thead className="bg-gray-50">
                  <tr>
                    {visibleColumns.has('job') && (
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Job
                      </th>
                    )}
                    {visibleColumns.has('customer') && (
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Customer
                      </th>
                    )}
                    {visibleColumns.has('completed') && (
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Completed
                      </th>
                    )}
                    {visibleColumns.has('technicians') && (
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Technicians
                      </th>
                    )}
                    {visibleColumns.has('photos') && (
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Photos
                      </th>
                    )}
                    {visibleColumns.has('status') && (
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                    )}
                    {visibleColumns.has('actions') && (
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredJobs.map((job) => {
                    const StatusIcon = getStatusIcon(job.status)
                    const completedDate = getCompletedDate(job)
                    const customerName = job.customer?.company_name || 'N/A'
                    const propertyAddress = job.property?.address || ''
                    const propertyCity = job.property?.city || ''
                    const propertyDisplay = `${propertyAddress}${propertyCity ? `, ${propertyCity}` : ''}` || 'N/A'
                    const techsDisplay = job.techs && job.techs.length > 0 ? job.techs.join(', ') : 'N/A'

                    return (
                      <tr
                        key={job.id}
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => handleView(job.id)}
                      >
                        {visibleColumns.has('job') && (
                          <td className="px-3 py-3 min-w-0">
                            <div>
                              <div className="text-sm font-medium text-gray-900 truncate">{job.title}</div>
                              <div className="text-sm text-gray-500 truncate">{propertyDisplay}</div>
                            </div>
                          </td>
                        )}
                        {visibleColumns.has('customer') && (
                          <td className="px-3 py-3 min-w-0 text-sm text-gray-900">
                            {customerName}
                          </td>
                        )}
                        {visibleColumns.has('completed') && (
                          <td className="px-3 py-3 min-w-0 text-sm text-gray-900">
                            {completedDate
                              ? new Date(completedDate).toLocaleDateString()
                              : 'N/A'}
                          </td>
                        )}
                        {visibleColumns.has('technicians') && (
                          <td className="px-3 py-3 min-w-0 text-sm text-gray-900">
                            {techsDisplay}
                          </td>
                        )}
                        {visibleColumns.has('photos') && (
                          <td className="px-3 py-3 min-w-0 text-sm text-gray-900">
                            {job.photos || 0}
                          </td>
                        )}
                        {visibleColumns.has('status') && (
                          <td className="px-3 py-3 min-w-0">
                            <div className="flex items-center gap-2">
                              <StatusIcon className="w-4 h-4" />
                              <span
                                className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                                  job.status
                                )}`}
                              >
                                {formatStatusLabel(job.status)}
                              </span>
                            </div>
                          </td>
                        )}
                        {visibleColumns.has('actions') && (
                          <td
                            className="px-3 py-3 min-w-0 text-sm font-medium"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="flex flex-wrap items-center gap-2">
                              <button
                                type="button"
                                onClick={(e) => openQuickView(job, e)}
                                className="text-blue-600 hover:text-blue-900"
                                title="Quick view"
                                aria-label="Quick view"
                              >
                                <EyeIcon className="w-4 h-4" />
                              </button>
                              {can('review.approve') &&
                                getDisplayStatus(job.status) === 'pending-review' && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleApprove(job.id)
                                  }}
                                  className="text-green-600 hover:text-green-900"
                                  title="Approve"
                                  aria-label="Approve"
                                >
                                  <CheckCircleIcon className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
      </div>

      {panelJob && (
        <ReviewJobQuickPanel
          job={panelJob}
          canApprove={can('review.approve')}
          onClose={() => setPanelJob(null)}
          onUpdated={async () => {
            await fetchJobs()
          }}
        />
      )}
    </MobilePage>

  )
}
